import { useMemo, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { apiVariable } from "../../../api/apiVariable";
import { DeliveryUnit } from "../../../data/HR/DeliveryUnit";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import { timeOffDayPortionLabel } from "../../../enums/HR/TimeOffDayPortionEnum";
import useAuth from "../../../hooks/useAuth";

const departments = [
  "All",
  "Dispatch",
  "Brokerage",
  "Safety",
  "Accounting",
  "Data Analytics Department",
  "HR",
  "Permits",
  "IT",
  "Management",
  "Operations",
  "Fleet",
  "Truck Sales",
  "Insurance",
  "Intermodal",
];

const dayBounds = (date: Date) => ({
  start: new Date(date.getFullYear(), date.getMonth(), date.getDate()),
  end: new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  ),
});

const normalizeRequest = (request: any, status?: string) => ({
  ...request,
  firstName: request.firstName || request.employee?.firstName,
  lastName: request.lastName || request.employee?.lastName,
  department: request.department || request.employee?.department,
  deliveryUnit: request.deliveryUnit || request.employee?.deliveryUnit,
  status: (status || request.status || "approved").toLowerCase(),
});

const DailyAvailability = () => {
  const { token, role } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [department, setDepartment] = useState("All");
  const [deliveryUnit, setDeliveryUnit] = useState("");
  const { start, end } = useMemo(() => dayBounds(date), [date]);

  const approvedQuery = useQuery({
    queryKey: ["dailyAvailability", date.toDateString()],
    queryFn: async () => {
      const { data } = await axios.get(`${apiVariable}/employeesAvailability`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { startDate: start.toISOString(), endDate: end.toISOString() },
      });
      return data;
    },
    enabled: !!token,
  });

  const pendingQuery = useQuery({
    queryKey: ["dailyAvailabilityPending"],
    queryFn: async () => {
      const { data } = await axios.get(`${apiVariable}/getAllTimeOffs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { pendingOnly: true },
      });
      return data;
    },
    enabled: !!token && !!role?.includes("HR"),
  });

  if (approvedQuery.isLoading || pendingQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={8}>
        <CircularProgress />
      </Box>
    );
  }
  if (approvedQuery.isError || pendingQuery.isError) {
    return (
      <Typography color="error" align="center" p={4}>
        Unable to load availability.
      </Typography>
    );
  }

  const entries = [
    ...(approvedQuery.data || [])
      .filter((item: any) => item.status?.toLowerCase() !== "pending")
      .map((item: any) => normalizeRequest(item)),
    ...(pendingQuery.data || []).map((item: any) =>
      normalizeRequest(item, "pending"),
    ),
  ].filter((item: any) => {
    const overlapsDay =
      new Date(item.startDate) <= end && new Date(item.endDate) >= start;
    const matchesDepartment =
      department === "All" || item.department === department;
    const matchesUnit =
      !deliveryUnit ||
      item.deliveryUnit?.toLowerCase() === deliveryUnit.toLowerCase();
    return overlapsDay && matchesDepartment && matchesUnit;
  });

  const changeDay = (amount: number) =>
    setDate(
      (current) =>
        new Date(
          current.getFullYear(),
          current.getMonth(),
          current.getDate() + amount,
        ),
    );

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, minWidth: 0 }}>
      <Box
        display="flex"
        justifyContent="center"
        gap={2}
        mb={2}
        flexWrap="wrap"
      >
        <FormControl sx={{ maxWidth: "100%", width: 200 }}>
          <InputLabel>Department</InputLabel>
          <Select
            value={department}
            label="Department"
            onChange={(event) => {
              setDepartment(event.target.value);
              setDeliveryUnit("");
            }}
          >
            {departments.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ maxWidth: "100%", width: 200 }}>
          <InputLabel>Delivery Unit</InputLabel>
          <Select
            value={deliveryUnit}
            label="Delivery Unit"
            onChange={(event) => setDeliveryUnit(event.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {DeliveryUnit.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <IconButton aria-label="Previous day" onClick={() => changeDay(-1)}>
            <ArrowBack />
          </IconButton>
          <Box textAlign="center" sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {date.toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </Typography>
            <Typography variant="caption">
              {entries.length} {entries.length === 1 ? "person" : "people"}{" "}
              unavailable or pending
            </Typography>
          </Box>
          <IconButton aria-label="Next day" onClick={() => changeDay(1)}>
            <ArrowForward />
          </IconButton>
        </Box>
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(min(260px, 100%), 1fr))"
          gap={2}
        >
          {entries.map((entry: any, index) => {
            const pending = entry.status === "pending";
            return (
              <Box
                key={`${entry.id || entry.firstName}-${index}`}
                role={pending ? "link" : undefined}
                tabIndex={pending ? 0 : undefined}
                onClick={() =>
                  pending &&
                  navigate(`/hr/time-off-requests?requestId=${entry.id}`)
                }
                onKeyDown={(event) => {
                  if (pending && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    navigate(`/hr/time-off-requests?requestId=${entry.id}`);
                  }
                }}
                sx={{
                  p: 2,
                  border: pending ? "2px dashed #ed6c02" : "1px solid",
                  borderColor: pending ? undefined : "divider",
                  borderRadius: pending ? 0 : 2,
                  clipPath: pending
                    ? "polygon(12px 0, 100% 0, calc(100% - 12px) 100%, 0 100%)"
                    : undefined,
                  bgcolor: pending ? "rgba(237,108,2,.10)" : "background.paper",
                  cursor: pending ? "pointer" : "default",
                  "&:hover": pending
                    ? { bgcolor: "rgba(237,108,2,.18)" }
                    : undefined,
                }}
              >
                <Typography fontWeight="bold">
                  {entry.firstName} {entry.lastName}
                </Typography>
                <Typography variant="body2">
                  {camelCaseToSentenceCase(entry.policy)} ·{" "}
                  {timeOffDayPortionLabel(entry.dayPortion, entry.isHalfDay)}
                </Typography>
                <Typography
                  variant="caption"
                  color={pending ? "warning.main" : "success.main"}
                  fontWeight="bold"
                >
                  {pending ? "PENDING — click to review" : "APPROVED"}
                </Typography>
              </Box>
            );
          })}
          {!entries.length && (
            <Typography color="text.secondary">
              No availability entries for this day.
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default DailyAvailability;
